/**
 * SKU to Barcode mapping utility
 * Maps PM-prefixed SKUs to their corresponding barcode format
 */

interface SkuMapping {
  prefix: string;
  barcodePrefix: string;
}

const SKU_MAPPINGS: SkuMapping[] = [
  { prefix: 'PM100', barcodePrefix: '200000' },
  { prefix: 'PM202', barcodePrefix: '200102' }, // More specific first
  { prefix: 'PM201', barcodePrefix: '200101' },
  { prefix: 'PM200', barcodePrefix: '200100' },
  { prefix: 'PM300', barcodePrefix: '200200' },
  { prefix: 'PM401', barcodePrefix: '200301' }, // More specific first
  { prefix: 'PM400', barcodePrefix: '200300' },
  { prefix: 'PM600', barcodePrefix: '200500' },
];

/**
 * Convert SKU to barcode based on mapping rules
 * @param sku - The product SKU (e.g., PM100xxxx)
 * @returns The corresponding barcode (e.g., 200000xxxx) or empty string if no match
 */
export function skuToBarcode(sku: string): string {
  if (!sku || typeof sku !== 'string') {
    return '';
  }

  // Normalize SKU (trim and uppercase)
  const normalizedSku = sku.trim().toUpperCase();

  // Find matching prefix (check more specific prefixes first)
  for (const mapping of SKU_MAPPINGS) {
    if (normalizedSku.startsWith(mapping.prefix)) {
      // Extract the suffix (remaining digits after prefix)
      const suffix = normalizedSku.substring(mapping.prefix.length);
      
      // Construct barcode: new prefix + original suffix
      return mapping.barcodePrefix + suffix;
    }
  }

  // No mapping found - return empty string
  return '';
}

/**
 * Validate if a SKU has a barcode mapping
 * @param sku - The product SKU
 * @returns true if SKU has a valid mapping
 */
export function hasValidSkuMapping(sku: string): boolean {
  if (!sku || typeof sku !== 'string') {
    return false;
  }

  const normalizedSku = sku.trim().toUpperCase();
  return SKU_MAPPINGS.some(mapping => normalizedSku.startsWith(mapping.prefix));
}

/**
 * Get the barcode prefix for a given SKU prefix
 * @param skuPrefix - The SKU prefix (e.g., 'PM100')
 * @returns The barcode prefix or null if not found
 */
export function getBarcodePrefix(skuPrefix: string): string | null {
  const mapping = SKU_MAPPINGS.find(m => m.prefix === skuPrefix);
  return mapping ? mapping.barcodePrefix : null;
}

/**
 * Convert barcode to SKU (reverse mapping)
 * @param barcode - The product barcode (e.g., 200000xxxx)
 * @returns The corresponding SKU (e.g., PM100xxxx) or empty string if no match
 */
export function barcodeToSku(barcode: string): string {
  if (!barcode || typeof barcode !== 'string') {
    return '';
  }

  // Normalize barcode (trim)
  const normalizedBarcode = barcode.trim();

  // Find matching barcode prefix
  for (const mapping of SKU_MAPPINGS) {
    if (normalizedBarcode.startsWith(mapping.barcodePrefix)) {
      // Extract the suffix (remaining digits after prefix)
      const suffix = normalizedBarcode.substring(mapping.barcodePrefix.length);
      
      // Construct SKU: SKU prefix + original suffix
      return mapping.prefix + suffix;
    }
  }

  // No mapping found - return empty string (might already be a SKU)
  return '';
}

/**
 * Auto-detect if input is SKU or barcode and convert to SKU
 * @param input - The input string (could be SKU or barcode)
 * @returns The SKU
 */
export function normalizeToSku(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  const trimmed = input.trim().toUpperCase();
  
  // If it starts with PM, it's already a SKU
  if (trimmed.startsWith('PM')) {
    return trimmed;
  }
  
  // Otherwise, try to convert from barcode to SKU
  const convertedSku = barcodeToSku(trimmed);
  
  // If conversion found a match, return it; otherwise return original input
  return convertedSku || trimmed;
}

/**
 * Get all available SKU mappings
 * @returns Array of all SKU to barcode mappings
 */
export function getAllSkuMappings(): SkuMapping[] {
  return [...SKU_MAPPINGS];
}

